/**
 * /equal-housing — Equal Housing / fair-tenancy policy statement.
 *
 * This is both:
 *   - A compliance trust signal for prospects browsing the marketing site
 *   - A concrete public reference our AI Listing drafter cites (the copywriter
 *     system prompt refuses to invent phrasing that would violate any of the
 *     protected classes listed below)
 *
 * All 13 provinces + federal grounds are named to make the page legally
 * useful, not just decorative.
 */
import { useEffect } from "react";
import { ShieldCheck, Scale, HeartHandshake, MessageSquare } from "lucide-react";

const PROTECTED_GROUNDS = [
  "Race, colour, or ethnic origin",
  "Ancestry or nationality",
  "Religion or creed",
  "Age",
  "Sex (including pregnancy and gender identity)",
  "Sexual orientation",
  "Gender identity or expression",
  "Marital or family status",
  "Physical or mental disability",
  "Source of income (including social assistance)",
  "Record of offences (subject to provincial rules)",
  "Language (in Québec and New Brunswick)",
  "Political belief (in some provinces)",
];

function setMeta(name, content) {
  let el = document.querySelector(`meta[name="${name}"]`) || document.querySelector(`meta[property="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(name.startsWith("og:") ? "property" : "name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}
function setCanonical(url) {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) { el = document.createElement("link"); el.setAttribute("rel", "canonical"); document.head.appendChild(el); }
  el.setAttribute("href", url);
}

export default function EqualHousing() {
  useEffect(() => {
    const title = "Equal Housing Policy — Central Rentals Canada";
    const desc = "Central Rentals is committed to equal opportunity in rental housing. Landlords using our platform agree to comply with the Canadian Human Rights Act and every provincial Human Rights Code — no discrimination in advertising, screening, or tenancy.";
    document.title = title;
    setMeta("description", desc);
    setMeta("og:title", title);
    setMeta("og:description", desc);
    setMeta("og:type", "article");
    setMeta("og:url", "https://centralrentals.ca/equal-housing");
    setCanonical("https://centralrentals.ca/equal-housing");
  }, []);

  return (
    <main className="min-h-screen bg-slate-50" data-testid="equal-housing-page">
      <header className="bg-gradient-to-br from-emerald-700 via-emerald-800 to-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-6 py-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-xs font-semibold uppercase tracking-wide mb-4">
            <ShieldCheck className="w-3.5 h-3.5" /> Fair-housing commitment
          </div>
          <h1 className="text-3xl md:text-5xl font-black leading-tight max-w-3xl">Equal housing policy</h1>
          <p className="mt-4 text-lg text-emerald-50 max-w-2xl">
            Central Rentals is committed to equal opportunity in rental housing.
            Every landlord using our platform agrees to comply with the
            <strong> Canadian Human Rights Act</strong> and every applicable
            provincial <strong>Human Rights Code</strong>.
          </p>
        </div>
      </header>

      <section className="max-w-3xl mx-auto px-6 py-14 space-y-10">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Scale className="w-5 h-5 text-emerald-700" />
            <h2 className="text-xl font-bold text-slate-900">Our commitment</h2>
          </div>
          <p className="text-slate-700 leading-relaxed">
            No listing, screening decision, lease term, or communication generated
            or sent through Central Rentals will discriminate against, harass, or
            treat unequally any person or group on any protected ground below.
            Our AI listing drafter is explicitly prompted to <em>refuse</em>
            language that references protected classes (e.g. &ldquo;young
            professionals only&rdquo;, &ldquo;no children&rdquo;,
            &ldquo;working couples preferred&rdquo;), and our tenant screening
            tools score applicants on objective criteria only: credit,
            payment history, income verification, and prior tenancy references.
          </p>
        </div>

        <div>
          <div className="flex items-center gap-2 mb-3">
            <HeartHandshake className="w-5 h-5 text-emerald-700" />
            <h2 className="text-xl font-bold text-slate-900">Protected grounds</h2>
          </div>
          <p className="text-slate-700 leading-relaxed mb-4">
            Landlords using Central Rentals may not refuse to rent, evict,
            impose different terms, or advertise a preference based on any of
            the following grounds:
          </p>
          <ul className="grid gap-2 sm:grid-cols-2">
            {PROTECTED_GROUNDS.map((g) => (
              <li key={g} className="flex gap-2 text-sm text-slate-800">
                <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                <span>{g}</span>
              </li>
            ))}
          </ul>
          <p className="text-xs text-slate-500 mt-4">
            Grounds vary slightly by province. Consult your provincial Human
            Rights Commission for the definitive list applicable to your rental.
          </p>
        </div>

        <div>
          <div className="flex items-center gap-2 mb-3">
            <MessageSquare className="w-5 h-5 text-emerald-700" />
            <h2 className="text-xl font-bold text-slate-900">Reporting</h2>
          </div>
          <p className="text-slate-700 leading-relaxed">
            If you believe a listing on Central Rentals or a landlord using our
            platform has discriminated against you, email
            {" "}<a href="mailto:contact@centralrentals.ca" className="text-emerald-700 font-semibold underline">contact@centralrentals.ca</a>{" "}
            with the listing URL and a description of what happened. We
            investigate every complaint within 5 business days and remove
            listings or suspend accounts that violate this policy. You may also
            file with your provincial Human Rights Commission.
          </p>
        </div>

        <div className="text-xs text-slate-500 pt-6 border-t border-slate-200">
          Last updated: February 2026 · This policy is a general commitment
          statement and is not legal advice.
        </div>
      </section>
    </main>
  );
}
